import { useMemo } from "react";
import {
  Lightning, WarningOctagon, Fan, Circuitry, ChartBar, BatteryFull, GridFour,
  CheckCircle, Warning, MinusCircle,
} from "@phosphor-icons/react";

/* v2.97 · Project Insights widget.
   Reads project.studies[] and shows the most-recent result per study
   kind, extracts a single meaningful metric, and colour-codes it
   against IEEE / IEC pass/fail thresholds so PMs get a one-glance
   electrical-design health check. */

const NUM = (v) => (typeof v === "number" && !Number.isNaN(v)) ? v : null;

/* Per-kind metric extractors. Each returns:
     { label, value, unit, status: "pass" | "warn" | "fail" | "info", detail }
   Handles varying key names defensively — `??` chained so partial
   result payloads still render. */
const EXTRACTORS = {
  load_flow: (r) => {
    const buses = r?.buses || r?.bus_results || [];
    const vmin = buses.length
      ? Math.min(...buses.map((b) => NUM(b.V_pu) ?? 1).filter((v) => v !== null))
      : NUM(r?.min_V_pu);
    const vmax = buses.length
      ? Math.max(...buses.map((b) => NUM(b.V_pu) ?? 1).filter((v) => v !== null))
      : NUM(r?.max_V_pu);
    if (vmin === null) return { label: "min bus V", value: "—", unit: "pu", status: "info" };
    // ANSI C84.1 recommends 0.95-1.05 pu at service voltage.
    const status = vmin >= 0.95 && (vmax ?? 1) <= 1.05 ? "pass"
                  : vmin >= 0.90 && (vmax ?? 1) <= 1.10 ? "warn" : "fail";
    return {
      label: "voltage range",
      value: `${vmin.toFixed(3)} – ${(vmax ?? vmin).toFixed(3)}`,
      unit: "pu",
      status,
      detail: r?.converged === false ? "did not converge" : `converged in ${r?.iterations ?? "?"} it.`,
    };
  },
  short_circuit: (r) => {
    // Look for a top-line 3P fault current in kA.
    const ka =
      NUM(r?.momentary?.i_symmetrical_ka) ??
      NUM(r?.momentary?.i_sc_ka)          ??
      NUM(r?.momentary?.i_peak_ka)        ??
      NUM(r?.i_sc_3p_ka)                  ??
      NUM(r?.results?.i_sc_ka);
    if (ka === null) return { label: "fault current", value: "—", unit: "kA", status: "info" };
    // For a common 480V bus 25kA is the standard AIC; >65kA is heavy duty.
    const status = ka <= 65 ? "pass" : "warn";
    // Extract motor contribution kA if present.
    const contribs = r?.momentary?.contributions || [];
    const motorKa = contribs
      .filter((c) => c.kind && c.kind !== "utility")
      .reduce((s, c) => s + (NUM(c.i_ka) || 0), 0);
    return {
      label: "3P fault (½ cy)", value: ka.toFixed(2), unit: "kA sym", status,
      detail: motorKa > 0 ? `+${motorKa.toFixed(2)} kA motors` : `peak ${r?.momentary?.i_peak_ka?.toFixed(1) || "?"} kA`,
    };
  },
  motor_starting: (r) => {
    const dip = NUM(r?.v_dip_pct) ?? NUM(r?.results?.v_dip_pct);
    if (dip === null) return { label: "V dip", value: "—", unit: "%", status: "info" };
    // IEEE 141 typical limit: 15% max transient dip.
    const status = dip <= 15 ? "pass" : dip <= 20 ? "warn" : "fail";
    return { label: "V dip @ start", value: dip.toFixed(1), unit: "%", status };
  },
  tcc: (r) => {
    const coord = r?.coordinated;
    if (coord === undefined) return { label: "coordination", value: "—", unit: "", status: "info" };
    return {
      label: "coordination",
      value: coord ? "OK" : "conflict",
      unit: "",
      status: coord ? "pass" : "fail",
      detail: r?.min_gap_s !== undefined ? `min gap ${r.min_gap_s.toFixed?.(2) ?? r.min_gap_s} s` : undefined,
    };
  },
  harmonics: (r) => {
    const thd = NUM(r?.thd_v_pct) ?? NUM(r?.THD_V_pct) ?? NUM(r?.results?.thd_v_pct);
    if (thd === null) return { label: "V THD", value: "—", unit: "%", status: "info" };
    // IEEE 519-2022 · V THD limit 5.0% at PCC for <1kV, 3% for MV.
    const status = thd <= 5 ? "pass" : thd <= 8 ? "warn" : "fail";
    return { label: "V THD", value: thd.toFixed(2), unit: "%", status };
  },
  battery_sizing: (r) => {
    const ah = NUM(r?.battery_ah) ?? NUM(r?.rated_ah) ?? NUM(r?.results?.battery_ah);
    if (ah === null) return { label: "sized capacity", value: "—", unit: "Ah", status: "info" };
    return { label: "sized capacity", value: ah.toFixed(0), unit: "Ah", status: "info" };
  },
  grounding: (r) => {
    // Compare calculated touch V to allowable.
    const tc = NUM(r?.touch_v_calc) ?? NUM(r?.touch_voltage) ?? NUM(r?.mesh_voltage);
    const tm = NUM(r?.touch_v_max)  ?? NUM(r?.touch_voltage_max);
    if (tc === null || tm === null) {
      const gpr = NUM(r?.gpr_v) ?? NUM(r?.GPR);
      if (gpr !== null) return { label: "GPR", value: `${(gpr / 1000).toFixed(2)}`, unit: "kV", status: "info" };
      return { label: "grid", value: "—", unit: "", status: "info" };
    }
    const status = tc <= tm ? "pass" : "fail";
    return {
      label: "touch V",
      value: `${tc.toFixed(0)} / ${tm.toFixed(0)}`,
      unit: "V",
      status,
      detail: "actual / allowable",
    };
  },
};

const KIND_META = {
  load_flow:      { label: "Load flow",     icon: Lightning       },
  short_circuit:  { label: "Short-circuit", icon: WarningOctagon  },
  motor_starting: { label: "Motor start",   icon: Fan             },
  tcc:            { label: "TCC coord.",    icon: Circuitry       },
  harmonics:      { label: "Harmonics",     icon: ChartBar        },
  battery_sizing: { label: "Battery",       icon: BatteryFull     },
  grounding:      { label: "Grounding",     icon: GridFour        },
};

const STATUS_META = {
  pass: { c: "#7CFF6B", icon: CheckCircle,  chip: "compliant" },
  warn: { c: "#FFCC00", icon: Warning,      chip: "watch"     },
  fail: { c: "#FF3B30", icon: WarningOctagon, chip: "action"  },
  info: { c: "#8792A2", icon: MinusCircle,  chip: "info"      },
};

function InsightCard({ kind, study }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const extractor = EXTRACTORS[kind];
  const insight = useMemo(() => (
    study && extractor ? extractor(study.result || {}) : null
  ), [study, extractor]);

  const status = insight?.status || "info";
  const S = STATUS_META[status];
  const StatusIcon = S.icon;

  return (
    <div className="border border-white/10 bg-[#12151C] p-3 flex flex-col justify-between min-h-[112px]"
         data-testid={`insight-${kind}`}
         style={{ boxShadow: `inset 3px 0 0 ${S.c}` }}>
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-gray-400">
            <Icon size={12} weight="fill" />
            <span className="uppercase tracking-widest text-[10px] font-mono">{meta.label}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest"
                style={{ color: S.c }}>
            <StatusIcon size={11} weight="fill" /> {S.chip}
          </span>
        </div>

        {study ? (
          <>
            <div className="mt-2 font-mono">
              <span className="text-2xl font-bold text-white">{insight?.value || "—"}</span>
              <span className="text-xs text-gray-500 ml-1">{insight?.unit}</span>
            </div>
            <div className="text-[10px] font-mono text-gray-500 mt-0.5">{insight?.label}</div>
          </>
        ) : (
          <div className="mt-2 text-xs font-mono text-gray-500">no runs yet</div>
        )}
      </div>
      {study && insight?.detail && (
        <div className="text-[9px] font-mono text-gray-500 mt-1 truncate" title={insight.detail}>
          {insight.detail}
        </div>
      )}
      {study && (
        <div className="text-[9px] font-mono text-gray-600 mt-1 truncate" title={study.name}>
          {study.name} · {new Date(study.saved_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

export default function InsightsWidget({ project }) {
  // Pick the most recent study per kind.
  const latest = useMemo(() => {
    const map = {};
    for (const s of project?.studies || []) {
      const cur = map[s.kind];
      if (!cur || new Date(s.saved_at) > new Date(cur.saved_at)) map[s.kind] = s;
    }
    return map;
  }, [project?.studies]);

  const total = Object.keys(latest).length;
  const passing = Object.keys(latest).reduce((n, k) => {
    const ins = EXTRACTORS[k]?.(latest[k].result || {});
    return n + (ins?.status === "pass" ? 1 : 0);
  }, 0);
  const failing = Object.keys(latest).reduce((n, k) => {
    const ins = EXTRACTORS[k]?.(latest[k].result || {});
    return n + (ins?.status === "fail" ? 1 : 0);
  }, 0);

  return (
    <div data-testid="project-insights">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="label-tag">// project · insights</div>
          <h3 className="font-display text-xl font-bold mt-0.5">Electrical design health</h3>
        </div>
        {total > 0 && (
          <div className="flex items-center gap-2 text-[10px] font-mono" data-testid="insights-tally">
            <span className="text-[#7CFF6B]">✓ {passing} compliant</span>
            <span className="text-gray-600">·</span>
            <span className="text-[#FF3B30]">! {failing} action needed</span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-400">{total}/7 studies run</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {Object.keys(KIND_META).map((k) => (
          <InsightCard key={k} kind={k} study={latest[k]} />
        ))}
      </div>
      {total === 0 && (
        <div className="mt-2 text-[10px] font-mono text-gray-500">
          Run studies from the Studies page and use <span className="text-[#00E5FF]">save</span> to
          attach them to this project — insights refresh automatically.
        </div>
      )}
    </div>
  );
}
